<script setup lang="ts">
import QrcodeVue from 'qrcode.vue'
import CopyLink from '@/components/CopyLink.vue'
const { t } = useI18n()
const { downloadQrCode, openInNewTab } = useDOM()
const { jwt } = useLocalStorage()
const downloadableQrcode = ref<HTMLElement | null>(null)

const imageSource = `${import.meta.env.VITE_AWS_STORAGE_URL}/contest-games/`

const FAQ = 'https://heypongohelp.zendesk.com/hc/fr/search?query=Jeux'
const flyerGenerator = computed(() => `https://visuals.heypongo.com/?source=addons&type=flyer&token=${jwt.value}`)

const props = defineProps<{
  shortLink: string
  fileName: string
  contactUrl: string
}>()

const cards = [
  {
    title: t('pages.contests.sharePage.store.title'),
    description: t('pages.contests.sharePage.store.description'),
    buttonText: t('pages.contests.sharePage.store.buttonText'),
    emitEvent: 'downloadQrCode',
    imageUrl: 'qr-code.png'
  },
  {
    title: t('pages.contests.sharePage.socialMedias.title'),
    description: t('pages.contests.sharePage.socialMedias.description'),
    input: true,
    imageUrl: 'social-medias.png'
  },
  {
    title: t('pages.contests.sharePage.website.title'),
    description: t('pages.contests.sharePage.website.description'),
    buttonText: t('pages.contests.sharePage.website.buttonText'),
    emitEvent: 'getContacted',
    imageUrl: 'share-website.png'
  }
]

const emitEvent = (eventName) => {
  if (eventName === 'downloadQrCode') {
    downloadQrCode(downloadableQrcode.value[0], props.fileName)
    return
  }
  if (eventName === 'getContacted') {
    openInNewTab(props.contactUrl)
  }
}

const qrcodeLink = computed(() => {
  let shortLink = props.shortLink
  if (!/^https?:\/\//i.test(shortLink)) {
    shortLink = `https://${shortLink}`
  }
  return shortLink
})
</script>
<template>
  <section>
    <div class="mb-10 grid grid-cols-1 items-stretch gap-10 md:grid-cols-2 md:flex-row lg:grid-cols-3">
      <Card v-for="(card, index) in cards" :key="index" class="overflow-hidden">
        <CardContent class="grid h-full">
          <div class="mb-6 flex h-44 justify-center rounded-md bg-primary/5 object-scale-down p-2">
            <div v-if="index === 0" class="flex flex-col justify-center">
              <div class="rounded-md border bg-white p-4">
                <qrcode-vue :value="qrcodeLink" :size="80" level="H" />
              </div>
              <div ref="downloadableQrcode" class="hidden">
                <qrcode-vue :value="qrcodeLink" :size="1000" level="H" />
              </div>
            </div>
            <img v-else :src="`${imageSource}${card.imageUrl}`" alt="Image" />
          </div>
          <div class="flex-col pb-6 text-center">
            <p class="mb-2 font-medium text-foreground">{{ card.title }}</p>
            <dfn class="text-sm font-medium not-italic text-muted-foreground">{{ card.description }}</dfn>
          </div>
          <div class="inline-flex w-full flex-wrap items-end justify-between gap-2">
            <CopyLink v-if="card.input" :link="shortLink" />
            <Button
              v-else
              variant="outline"
              :class="index !== 0 ? 'w-full' : ''"
              class="grow"
              @click="emitEvent(card.emitEvent)"
            >
              <span class="py-1">
                {{ card.buttonText }}
              </span>
            </Button>
            <div v-if="index === 0" class="grow">
              <Button variant="default-primary" class="w-full" @click="openInNewTab(flyerGenerator)">
                <span class="py-1">{{ t('pages.contests.sharePage.store.generateFlyer') }}</span>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>

    <p class="inline-flex items-center gap-1 text-sm font-medium text-primary">
      <BaseIcon name="HeroQuestionMarkCircleIconSolid" class="aspect-square h-6 w-6" />
      {{ t('pages.contests.sharePage.faqQuestions') }}
      <a :href="`${FAQ}`" target="_blank" class="underline"> FAQ</a>
    </p>
  </section>
</template>
